/**
 * @typedef {FormatMailtoContentInput}
 * @property {string[]} bodyLines optional strings to be used for each line of the body of the email.
 * An empty line will be inserted between each line.
 * @property {string} email the email address
 * @property {string} subject optional subject for the email
 */
interface FormatMailtoContentInput {
    bodyLines?: string[];
    email: string;
    subject?: string;
}

/**
 * Takes a object with email details and converts it into the content required for the `href`
 * attribute of an `<a>` element to be used as a mailto link.
 * @param {FormatMailtoContentInput} input
 * @returns {string} the formatted href content
 */
export const formatMailtoContent = ({
    bodyLines,
    email,
    subject,
}: FormatMailtoContentInput): string => {
    const subjectContent = subject
        ? `subject=${encodeURIComponent(subject)}`
        : '';

    const bodyLinesContent = (bodyLines ?? ([] as string[])).map(
        (bodyLine: string): string => encodeURIComponent(bodyLine),
    );

    const bodyContent = `body=${bodyLinesContent.join('%0A%0A')}`;

    if (subjectContent.length > 0 && (bodyLines?.length ?? 0) === 0) {
        return `mailto:${email}?${subjectContent}`;
    }

    if (subjectContent.length > 0 && (bodyLines?.length ?? 0) > 0) {
        return `mailto:${email}?${subjectContent}&${bodyContent}`;
    }

    if (subjectContent.length === 0 && (bodyLines?.length ?? 0) > 0) {
        return `mailto:${email}?${bodyContent}`;
    }

    return `mailto:${email}`;
};
